"use client";

import { useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { Button } from "@/components/forms/Button";
import { Typography } from "./Typography";

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  /** Rendered in the header row, between the titles and the close button. */
  headerAction?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  side?: "left" | "right";
  size?: "sm" | "md" | "lg" | "xl" | "full";
  showOverlay?: boolean;
  closeOnOverlayClick?: boolean;
  /**
   * Set false while a Dialog is open inside the drawer. Both components attach
   * their own document-level Escape handler, so one keypress would otherwise
   * dismiss the dialog and close the whole drawer behind it.
   */
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  className?: string;
}

const sizeStyles = {
  sm: "w-full sm:w-[30%] sm:min-w-[320px]",
  md: "w-full sm:w-[40%] sm:min-w-[400px]",
  lg: "w-full sm:w-[50%] sm:min-w-[500px]",
  xl: "w-full sm:w-[60%] sm:min-w-[600px]",
  full: "w-full sm:w-[90%]",
};

export function Drawer({
  isOpen,
  onClose,
  title,
  subtitle,
  headerAction,
  children,
  footer,
  side = "right",
  size = "md",
  showOverlay = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  className = "",
}: DrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement;
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      previousActiveElement.current?.focus();
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!closeOnEscape) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, handleClose, closeOnEscape]);

  useEffect(() => {
    if (!isOpen || !drawerRef.current) return;

    const drawer = drawerRef.current;
    const focusableElements = drawer.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    drawer.addEventListener("keydown", handleTabKey as EventListener);
    firstElement?.focus();

    return () => {
      drawer.removeEventListener("keydown", handleTabKey as EventListener);
    };
  }, [isOpen]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget && closeOnOverlayClick) {
        handleClose();
      }
    },
    [closeOnOverlayClick, handleClose]
  );

  if (!isOpen) return null;

  if (typeof window === "undefined") return null;

  const drawerContent = (
    <>
      {showOverlay && (
        <div
          className="fixed inset-0 [body[data-environment-banner]_&]:top-7 bg-black/30 backdrop-blur-sm z-40 transition-opacity duration-300 opacity-100"
          onClick={handleOverlayClick}
          aria-hidden="true"
        />
      )}

      <div
        ref={drawerRef}
        className={cn(
          "fixed top-0 [body[data-environment-banner]_&]:top-7 bottom-0 bg-white shadow-2xl z-50 flex flex-col translate-x-0",
          "transition-transform duration-500 ease-in-out",
          side === "right" ? "right-0" : "left-0",
          sizeStyles[size],
          className
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "drawer-title" : undefined}
        aria-describedby={subtitle ? "drawer-subtitle" : undefined}
      >
        {(title || subtitle || showCloseButton || headerAction) && (
          <div className="shrink-0 flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex-1 pr-4">
              {title && (
                <Typography variant="headline3" as="h2" leading="leading-tight" id="drawer-title">
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography variant="body" color="muted" leading="leading-tight" id="drawer-subtitle" className="mt-1">
                  {subtitle}
                </Typography>
              )}
            </div>
            <div className="flex items-center gap-6 shrink-0">
              {headerAction && (
                <div className="flex items-center gap-3">{headerAction}</div>
              )}
              {showCloseButton && (
                <Button
                  iconOnly
                  icon={X}
                  variant="ghost"
                  size="md"
                  onClick={handleClose}
                  aria-label="Close drawer"
                  type="button"
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 border-transparent"
                />
              )}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">{children}</div>

        {footer && (
          <div className="shrink-0 flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            {footer}
          </div>
        )}
      </div>
    </>
  );

  if (typeof window === "undefined") return null;

  return createPortal(drawerContent, document.body);
}
